/** @jsx jsx */

import _ from 'lodash';
import { Fragment, useEffect, useState } from 'react';

import { jsx, css } from '@emotion/core';

import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import RadioArray from 'app/common/components/RadioArray';
import { BASE_DOC_URL_VB, FIELD_KEYS } from 'app/developer-v3/constants';

import StaticDropdownInputField from '../StaticDropdownInputField';
import SelectTriggerOutputField from '../SelectTriggerOutputField';

import type {
  DropdownConfigs,
  DropdownType,
  FormifiedDynamicDropdown,
  InfoMeta,
} from 'app/developer-v3/types';
import type { ErrorList } from 'app/developer-v3/types/validator';
import type { StaticDropdown } from 'app/developer-v3/platformSchema/field';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

const Styles = {
  typeWrapper: css`
    display: flex;
  `,
};

type Props = {
  addsSearchfill: boolean;
  dropdownConfigs: DropdownConfigs;
  errors: ErrorList | string;
  info?: InfoMeta;
  onChangeType: (value: string) => void;
  setDropdown: (
    type: DropdownType,
    dropdown: FormifiedDynamicDropdown | StaticDropdown
  ) => void;
  titleSource: string;
  titleType: string;
  triggers: Array<TriggerSchema>;
};

// TODO: Remove this component with DropdownInputFieldZinnia when all its
// consumers are coverted to Zinnia.
function DropdownInputField(props: Props) {
  const {
    addsSearchfill,
    dropdownConfigs,
    errors,
    info,
    onChangeType,
    setDropdown,
    titleSource,
    titleType,
    triggers,
  } = props;
  const { choices, dynamic } = dropdownConfigs;

  const [type, setType] = useState(
    dynamic ? FIELD_KEYS.dynamic : FIELD_KEYS.choices
  );

  useEffect(() => {
    setType(dynamic ? FIELD_KEYS.dynamic : FIELD_KEYS.choices);
  }, [dynamic]);

  const onTypeChange = nextType => {
    setType(nextType);
    onChangeType(nextType);

    if (nextType === FIELD_KEYS.dynamic) {
      setDropdown(FIELD_KEYS.dynamic, {});
    }
  };

  return (
    <Fragment>
      <NonFormaticFieldWrapper
        helpText={
          addsSearchfill &&
          'Static Dropdown shows a list of pre-set options; Dynamic Dropdown shows a list of options from a trigger.'
        }
        title={titleType}
      >
        <div css={Styles.typeWrapper}>
          <RadioArray
            choices={[
              { label: 'Dynamic', value: FIELD_KEYS.dynamic },
              { label: 'Static', value: FIELD_KEYS.choices },
            ]}
            onChange={onTypeChange}
            value={type}
          />
        </div>
      </NonFormaticFieldWrapper>
      {type === FIELD_KEYS.dynamic ? (
        <NonFormaticFieldWrapper
          helpText={`Choose the trigger and field to populate this menu. <a target="_blank" href="${BASE_DOC_URL_VB}/build/input-designer#dynamic">Learn more</a>.`}
          required={true}
          title={titleSource}
        >
          <div>
            <SelectTriggerOutputField
              errors={_.get(errors, [type], {})}
              info={_.get(info, [type], {})}
              onChange={value => setDropdown(type, value)}
              triggers={triggers}
              value={dynamic}
            />
          </div>
        </NonFormaticFieldWrapper>
      ) : (
        <StaticDropdownInputField
          initValue={choices}
          isRequired={true}
          onChange={value => setDropdown(type, value)}
          title={titleSource}
        />
      )}
    </Fragment>
  );
}

DropdownInputField.defaultProps = {
  addsSearchfill: false,
  onChangeType: _.noop,
  titleSource: 'Dropdown Source',
  titleType: 'Dropdown Type',
};

export default DropdownInputField;
